import React, { useState } from 'react';
import { X, MessageSquare } from 'lucide-react';
import { candidateCommentsService } from '../../services/candidateCommentsService';
import './CandidateToolbarModals.scss';

interface WriteCommentModalProps {
  candidateId: number;
  candidateName: string;
  initialComment?: string;
  onClose: () => void;
  onSaved?: (comment: string) => void;
}

/**
 * Per-employer × per-candidate private note. Backed by the existing
 * `/api/candidate-comments` endpoints — the same comment shows up
 * across the application (job applications, atrankas, CV detail page).
 *
 * Submitting an empty body is treated as "remove the existing comment"
 * to match the behaviour we already use elsewhere (JobApplicationsPage).
 */
const WriteCommentModal: React.FC<WriteCommentModalProps> = ({
  candidateId,
  candidateName,
  initialComment = '',
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState(initialComment);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (trimmed.length > 5000) {
      setError('Komentaras per ilgas (iki 5000 simbolių).');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      if (trimmed.length === 0) {
        await candidateCommentsService.remove(candidateId);
        onSaved?.('');
      } else {
        const { comment } = await candidateCommentsService.save(candidateId, trimmed);
        onSaved?.(comment.comment);
      }
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Nepavyko išsaugoti komentaro');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="candidate-toolbar-modal-overlay" onClick={submitting ? undefined : onClose}>
      <div
        className="candidate-toolbar-modal candidate-toolbar-modal--comment"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="candidate-toolbar-modal__close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Uždaryti"
        >
          <X size={20} />
        </button>

        <div className="candidate-toolbar-modal__header">
          <MessageSquare size={20} />
          <h2>Komentaras apie kandidatą</h2>
        </div>

        <p className="candidate-toolbar-modal__subtitle">
          {candidateName} — komentaras matomas tik jūsų komandai.
        </p>

        <textarea
          className="candidate-toolbar-modal__textarea"
          rows={6}
          maxLength={5000}
          placeholder="Pvz. stiprus EN, ieškant tinkamo skelbimo..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={submitting}
          autoFocus
        />

        {error && <div className="candidate-toolbar-modal__error">{error}</div>}

        <div className="candidate-toolbar-modal__actions">
          <button
            type="button"
            className="candidate-toolbar-modal__btn candidate-toolbar-modal__btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Atšaukti
          </button>
          <button
            type="button"
            className="candidate-toolbar-modal__btn candidate-toolbar-modal__btn--primary"
            onClick={handleSave}
            disabled={submitting}
          >
            {submitting ? 'Saugoma...' : 'Išsaugoti'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WriteCommentModal;
